import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FactoryIcon from "@mui/icons-material/Factory";
import TimelineIcon from "@mui/icons-material/Timeline";
import {
  Box,
  Chip,
  DialogContent,
  DialogContentText,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { MergeStepFooter } from "components/MergePicker/MergeStepFooter";
import { SelectableCellContent } from "components/MergePicker/styles";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { safeParseFloat } from "encamp-shared/src/utils/safeParseNumber";
import { DensityUnits, InventoryOverlapResolutionStrategy } from "generated-graphql/graphql";
import { groupBy } from "lodash-es";
import pluralize from "pluralize";
import { useState } from "react";

import {
  ATTRIBUTE_LABELS,
  combinedMaxAmountPreview,
  combineRequiresDensity,
  CombineTargetProfile,
  findCombineConflicts,
  findMeasurementCollisionLocationNames,
  formatEntryAmount,
  formatMeasurementLocationsLabel,
  formatMeasurementPeaksLabel,
  formatMeasurementReadingsLabel,
  hasMeasurementSummaries,
  InventoryOverlap,
  InventoryOverlapEntry,
  inventoryOverlapKey,
  InventoryOverlapResolutionState,
  isInventoryOverlapResolved,
  stateFieldConflictKey,
  storageLocationConflictKey,
} from "./inventoryOverlaps";

const DENSITY_UNITS_VALUES = new Set<string>(Object.values(DensityUnits));

function isDensityUnitsValue(value: string): value is DensityUnits {
  return DENSITY_UNITS_VALUES.has(value);
}

const STRATEGY_VALUES = new Set<string>(Object.values(InventoryOverlapResolutionStrategy));

function isStrategyValue(value: string): value is InventoryOverlapResolutionStrategy {
  return STRATEGY_VALUES.has(value);
}

function storageTypesLabel(storageTypes: string[], storageTypeDescription: string | null): string {
  const typesLabel = storageTypes.length > 0 ? storageTypes.join(", ") : "No storage type";
  return storageTypeDescription ? `${typesLabel} (${storageTypeDescription})` : typesLabel;
}

type SelectableOptionCellProps = {
  /** Accessible name, formatted as "<value> (<chemicals>)". */
  label: string;
  display: string;
  caption: string;
  selected: boolean;
  onSelect: () => void;
};

/**
 * A pick-by-clicking option, reusing the merge table's SelectableCellContent
 * so conflict picks look and behave like the cell selections on the Review
 * details step. The wrapper restores the radio role, accessible name, and
 * keyboard support the plain clickable cell doesn't provide.
 */
function SelectableOptionCell({
  label,
  display,
  caption,
  selected,
  onSelect,
}: SelectableOptionCellProps) {
  return (
    <Box
      role="radio"
      aria-checked={selected}
      aria-label={label}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === " " || event.key === "Enter") {
          event.preventDefault();
          onSelect();
        }
      }}
      sx={{ height: "100%", alignSelf: "stretch" }}
    >
      <SelectableCellContent isSelected={selected} style={{ flexDirection: "column" }}>
        <Typography variant="body2">{display}</Typography>
        <Typography variant="caption" color="text.secondary" component="div">
          {caption}
        </Typography>
      </SelectableCellContent>
    </Box>
  );
}

type KeepColumnCellProps = {
  /**
   * Present on the column's header cell, which carries the radio semantics
   * and testid for the whole column. Value cells below it are click targets
   * only, so screen readers see exactly one radio per chemical.
   */
  radio?: { label: string; testId: string };
  selected: boolean;
  hovered: boolean;
  onSelect: () => void;
  onHoverChange: (hovered: boolean) => void;
  children: React.ReactNode;
};

/**
 * One cell of a "Keep one" chemical column. The whole column is a single
 * pick target: hover state lives on the card so every cell in the hovered
 * column highlights together, matching the Review details step's cells.
 */
function KeepColumnCell({
  radio,
  selected,
  hovered,
  onSelect,
  onHoverChange,
  children,
}: KeepColumnCellProps) {
  return (
    <Box
      role={radio ? "radio" : undefined}
      aria-checked={radio ? selected : undefined}
      aria-label={radio?.label}
      tabIndex={radio ? 0 : undefined}
      data-testid={radio?.testId}
      onClick={onSelect}
      onKeyDown={
        radio
          ? (event: React.KeyboardEvent) => {
              if (event.key === " " || event.key === "Enter") {
                event.preventDefault();
                onSelect();
              }
            }
          : undefined
      }
      sx={{ height: "100%" }}
    >
      <SelectableCellContent
        isSelected={selected}
        isHovered={hovered}
        onHoverChange={onHoverChange}
      >
        {children}
      </SelectableCellContent>
    </Box>
  );
}

type CombineDensity = { density: number | null; densityUnits: DensityUnits | null };

type ResolveInventoryOverlapsStepProps = {
  inventoryOverlaps: InventoryOverlap[];
  resolutions: Record<string, InventoryOverlapResolutionState>;
  onStrategyChange: (key: string, strategy: InventoryOverlapResolutionStrategy) => void;
  onKeepSelect: (key: string, keepChemicalId: string) => void;
  onFieldChoice: (key: string, conflictKey: string, choice: string) => void;
  onBatchStrategyChange: (strategy: InventoryOverlapResolutionStrategy) => void;
  onBack: () => void;
  onCancel: () => void;
  footerStep?: { current: number; total: number };
  onMerge: () => void;
  isMerging: boolean;
  mergedChemicalName: string;
  affectedFacilityCount: number;
  // Whether the merged chemical (Step 1 selection, possibly overridden by
  // combineDensity below) has a density. Gates every density-requiring
  // COMBINE's resolved state.
  mergedHasDensity: boolean;
  // Whether the Step 1 selection itself carries a density, ignoring the
  // inline combineDensity entry. Gates the inline density block's visibility:
  // a value typed inline must stay on screen (reviewable and editable) until
  // the merge, so the block only disappears when Step 1 supplies the density.
  mergedInputHasDensity: boolean;
  combineDensity: CombineDensity;
  onCombineDensityChange: (next: CombineDensity) => void;
  // The merged record's state-of-matter/physical-hazard classification —
  // feeds resolveCombineTargetUnit inside combineRequiresDensity.
  mergedProfile: CombineTargetProfile;
};

function renderStorageLocations(storageLocationNames: string[]) {
  if (storageLocationNames.length === 0) {
    return "—";
  }

  return storageLocationNames.map((storageLocationName) => (
    <Box key={storageLocationName}>{storageLocationName}</Box>
  ));
}

function renderKeepMergedAmount(selectedEntry: InventoryOverlapEntry | undefined) {
  if (!selectedEntry) {
    return (
      <>
        {"— "}
        <Typography variant="caption" color="text.secondary">
          select a chemical to keep
        </Typography>
      </>
    );
  }

  return (
    <>
      {formatEntryAmount(selectedEntry)}
      <Typography variant="caption" color="text.secondary">
        {` from ${selectedEntry.chemicalName}`}
      </Typography>
    </>
  );
}

function renderMergedAmount(
  inventoryOverlap: InventoryOverlap,
  resolution: InventoryOverlapResolutionState,
  selectedEntry: InventoryOverlapEntry | undefined
) {
  const hasMeasurements = hasMeasurementSummaries(inventoryOverlap);
  if (resolution.strategy === InventoryOverlapResolutionStrategy.Blank) {
    if (hasMeasurements) {
      return (
        <>
          {"— "}
          <Typography variant="caption" color="text.secondary">
            {`left blank; a zero reading dated Jan 1, ${inventoryOverlap.reportingYear}, flagged for review`}
          </Typography>
        </>
      );
    }
    return (
      <>
        {"— "}
        <Typography variant="caption" color="text.secondary">
          left blank, flagged for review
        </Typography>
      </>
    );
  }
  if (resolution.strategy === InventoryOverlapResolutionStrategy.Combine) {
    // There is no client-side total for a series union — every reading
    // survives the merge, so the sum only exists after re-aggregation.
    if (hasMeasurements) {
      return (
        <Typography variant="body2" color="text.secondary">
          keeps every reading from both entries; totals recalculate after the merge
        </Typography>
      );
    }
    // The client can't do density math, so a summed preview is only
    // trustworthy when every entry already agrees on a unit.
    if (
      inventoryOverlap.hasSharedChemicalUnit &&
      !inventoryOverlap.hasStorageLocationUnitConflict
    ) {
      return combinedMaxAmountPreview(inventoryOverlap);
    }
    return (
      <>
        {inventoryOverlap.entries.map((entry) => formatEntryAmount(entry)).join(" + ")}
        <Typography variant="caption" color="text.secondary" component="div">
          total calculated after merge
        </Typography>
      </>
    );
  }
  return renderKeepMergedAmount(selectedEntry);
}

function renderMeasurementSummary(entry: InventoryOverlapEntry) {
  if (!entry.measurementSummary) {
    return "—";
  }
  const summary = entry.measurementSummary;
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2">{formatMeasurementReadingsLabel(summary)}</Typography>
      <Typography variant="body2">{formatMeasurementPeaksLabel(summary)}</Typography>
      <Typography variant="body2">{formatMeasurementLocationsLabel(summary)}</Typography>
    </Stack>
  );
}

function renderMergedStorage(
  inventoryOverlap: InventoryOverlap,
  resolution: InventoryOverlapResolutionState,
  selectedEntry: InventoryOverlapEntry | undefined
) {
  if (resolution.strategy === InventoryOverlapResolutionStrategy.Blank) {
    return renderStorageLocations([]);
  }
  if (resolution.strategy === InventoryOverlapResolutionStrategy.Combine) {
    return renderStorageLocations(
      Array.from(new Set(inventoryOverlap.entries.flatMap((e) => e.storageLocationNames)))
    );
  }
  return renderStorageLocations(selectedEntry?.storageLocationNames ?? []);
}

type InventoryOverlapCardProps = {
  inventoryOverlap: InventoryOverlap;
  resolution: InventoryOverlapResolutionState;
  onStrategyChange: (strategy: InventoryOverlapResolutionStrategy) => void;
  onKeepSelect: (keepChemicalId: string) => void;
  onFieldChoice: (conflictKey: string, choice: string) => void;
};

function InventoryOverlapCard({
  inventoryOverlap,
  resolution,
  onStrategyChange,
  onKeepSelect,
  onFieldChoice,
}: InventoryOverlapCardProps) {
  const key = inventoryOverlapKey(inventoryOverlap);
  const isCombining = resolution.strategy === InventoryOverlapResolutionStrategy.Combine;
  const isKeeping = resolution.strategy === InventoryOverlapResolutionStrategy.Keep;
  const selectedEntry = inventoryOverlap.entries.find(
    (entry) => entry.chemicalId === resolution.keepChemicalId
  );
  const conflicts = findCombineConflicts(inventoryOverlap);
  const hasMeasurements = hasMeasurementSummaries(inventoryOverlap);
  const combineDisabledTooltip = inventoryOverlap.hasMeasurementStorageLocationCollision
    ? `These measurement series share ${findMeasurementCollisionLocationNames(
        inventoryOverlap.entries
      ).join(", ")} — keep one entry.`
    : "";

  const [hoveredChemicalId, setHoveredChemicalId] = useState<string | null>(null);

  // Shared by all three cells of a chemical's column so they select and
  // highlight as one target.
  const keepCellProps = (chemicalId: string) => ({
    selected: chemicalId === resolution.keepChemicalId,
    hovered: chemicalId === hoveredChemicalId,
    onSelect: () => onKeepSelect(chemicalId),
    onHoverChange: (hovered: boolean) => setHoveredChemicalId(hovered ? chemicalId : null),
  });

  // SelectableCellContent brings its own padding; the wrapped cells shrink
  // theirs so the clickable area fills the cell. height: "inherit" pairs with
  // the 1px row-height hack below to stretch the content to the tallest cell
  // in the row (same trick as MergePicker's StyledTableRow).
  const keepCellSx = isKeeping ? { p: 0.5, height: "inherit" } : undefined;
  const keepRowSx = isKeeping ? { height: "1px" } : undefined;

  // One shared grid template for every picker row in the conflicts section, so
  // the radio options line up in columns across attributes and state fields.
  const optionColumnCount = Math.max(
    2,
    ...conflicts.storageLocations.flatMap((location) =>
      location.conflicts.map((conflict) => conflict.options.length)
    ),
    ...conflicts.stateFieldConflicts.map((conflict) => conflict.options.length)
  );
  const conflictRowSx = {
    mt: 1,
    display: "grid",
    gridTemplateColumns: `180px repeat(${optionColumnCount}, minmax(0, 1fr))`,
    // 8px, matching the horizontal gap between the merge table's cells.
    columnGap: 1,
    alignItems: "center",
  };
  // Rows under a storage-location header indent to read as its children; the
  // label column narrows by the same 24px so the option columns still line up
  // with the un-indented state-field rows.
  const indentedConflictRowSx = {
    ...conflictRowSx,
    pl: 3,
    gridTemplateColumns: `156px repeat(${optionColumnCount}, minmax(0, 1fr))`,
  };

  return (
    <Paper variant="outlined" data-testid={`inventory-overlap-${key}`} sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">{inventoryOverlap.reportingYear}</Typography>
        {inventoryOverlap.hasIdenticalAmounts && (
          <Chip icon={<ContentCopyIcon />} label="identical amounts" size="small" />
        )}
        {hasMeasurements && <Chip icon={<TimelineIcon />} label="Measurements" size="small" />}
      </Stack>
      <RadioGroup
        value={resolution.strategy}
        onChange={(event) => {
          if (isStrategyValue(event.target.value)) onStrategyChange(event.target.value);
        }}
        data-testid={`inventory-overlap-strategy-${key}`}
        sx={{ mb: 2 }}
      >
        <FormControlLabel
          value={InventoryOverlapResolutionStrategy.Keep}
          control={<Radio size="small" />}
          label={
            <Typography variant="body2">
              <b>Keep one</b>
              <Typography component="span" variant="body2" color="text.secondary">
                {" — choose one record, ignore the merge for this report"}
              </Typography>
            </Typography>
          }
        />
        <Tooltip title={combineDisabledTooltip} disableHoverListener={!combineDisabledTooltip}>
          <span>
            <FormControlLabel
              value={InventoryOverlapResolutionStrategy.Combine}
              disabled={inventoryOverlap.hasMeasurementStorageLocationCollision}
              control={<Radio size="small" />}
              label={
                <Typography variant="body2">
                  <b>Combine both</b>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {" — merge inventory totals, resolve conflicting data"}
                  </Typography>
                </Typography>
              }
            />
          </span>
        </Tooltip>
      </RadioGroup>
      {/* Fixed layout keeps column widths stable as selections change cell content. */}
      <Table size="small" sx={{ tableLayout: "fixed" }}>
        <TableHead>
          <TableRow sx={keepRowSx}>
            <TableCell sx={{ width: "12%" }} />
            {inventoryOverlap.entries.map((entry) => (
              <TableCell key={entry.chemicalId} sx={keepCellSx}>
                {isKeeping ? (
                  <KeepColumnCell
                    radio={{
                      label: `Keep ${entry.chemicalName}`,
                      testId: `inventory-overlap-keep-${key}-${entry.chemicalId}`,
                    }}
                    {...keepCellProps(entry.chemicalId)}
                  >
                    {entry.chemicalName}
                  </KeepColumnCell>
                ) : (
                  entry.chemicalName
                )}
              </TableCell>
            ))}
            {!isKeeping && (
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  Merged inventory
                </Typography>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow sx={keepRowSx}>
            <TableCell component="th" scope="row">
              Amount
            </TableCell>
            {inventoryOverlap.entries.map((entry) => (
              <TableCell key={entry.chemicalId} sx={keepCellSx}>
                {isKeeping ? (
                  <KeepColumnCell {...keepCellProps(entry.chemicalId)}>
                    {formatEntryAmount(entry)}
                  </KeepColumnCell>
                ) : (
                  formatEntryAmount(entry)
                )}
              </TableCell>
            ))}
            {!isKeeping && (
              <TableCell data-testid={`inventory-overlap-merged-amount-${key}`}>
                {renderMergedAmount(inventoryOverlap, resolution, selectedEntry)}
              </TableCell>
            )}
          </TableRow>
          <TableRow sx={keepRowSx}>
            <TableCell component="th" scope="row">
              Storage
            </TableCell>
            {inventoryOverlap.entries.map((entry) => (
              <TableCell key={entry.chemicalId} sx={keepCellSx}>
                {isKeeping ? (
                  <KeepColumnCell {...keepCellProps(entry.chemicalId)}>
                    {renderStorageLocations(entry.storageLocationNames)}
                  </KeepColumnCell>
                ) : (
                  renderStorageLocations(entry.storageLocationNames)
                )}
              </TableCell>
            ))}
            {!isKeeping && (
              <TableCell data-testid={`inventory-overlap-merged-storage-${key}`}>
                {renderMergedStorage(inventoryOverlap, resolution, selectedEntry)}
              </TableCell>
            )}
          </TableRow>
          {hasMeasurements && (
            <TableRow sx={keepRowSx}>
              <TableCell component="th" scope="row">
                Measurement series
              </TableCell>
              {inventoryOverlap.entries.map((entry) => (
                <TableCell key={entry.chemicalId} sx={keepCellSx}>
                  {isKeeping ? (
                    <KeepColumnCell {...keepCellProps(entry.chemicalId)}>
                      {renderMeasurementSummary(entry)}
                    </KeepColumnCell>
                  ) : (
                    renderMeasurementSummary(entry)
                  )}
                </TableCell>
              ))}
              {!isKeeping && <TableCell>—</TableCell>}
            </TableRow>
          )}
        </TableBody>
      </Table>
      {isCombining &&
        (conflicts.storageLocations.length > 0 || conflicts.stateFieldConflicts.length > 0) && (
          <Stack spacing={1.5} sx={{ mt: 4 }} data-testid={`inventory-overlap-conflicts-${key}`}>
            <Typography variant="subtitle1" fontWeight="bold">
              Resolve conflicting values
            </Typography>
            {conflicts.storageLocations.map((location) => (
              <Box
                key={`${location.storageLocationId}|${JSON.stringify(location.storageTypes)}|${
                  location.storageTypeDescription ?? ""
                }`}
              >
                <Stack direction="row" spacing={1} alignItems="baseline">
                  <Typography variant="subtitle2">{location.storageLocationName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {storageTypesLabel(location.storageTypes, location.storageTypeDescription)}
                  </Typography>
                </Stack>
                {location.context.length > 0 && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {location.context.map((c) => `${c.label}: ${c.display}`).join(" · ")}
                  </Typography>
                )}
                {location.conflicts.map((conflict) => {
                  const conflictKey = storageLocationConflictKey(location, conflict.attribute);
                  return (
                    <Box key={conflict.attribute} sx={indentedConflictRowSx}>
                      <Typography variant="body2">
                        {ATTRIBUTE_LABELS[conflict.attribute]}
                      </Typography>
                      {/* display: contents lifts each option into the shared grid columns. */}
                      <Box
                        role="radiogroup"
                        aria-label={ATTRIBUTE_LABELS[conflict.attribute]}
                        sx={{ display: "contents" }}
                      >
                        {conflict.options.map((option) => (
                          <SelectableOptionCell
                            key={option.serializedValue}
                            label={`${option.display} (${option.chemicalNames.join(", ")})`}
                            display={option.display}
                            caption={option.chemicalNames.join(", ")}
                            selected={
                              resolution.fieldChoices[conflictKey] === option.serializedValue
                            }
                            onSelect={() => onFieldChoice(conflictKey, option.serializedValue)}
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))}
            {conflicts.storageLocations.length > 0 && conflicts.stateFieldConflicts.length > 0 && (
              <Divider />
            )}
            {conflicts.stateFieldConflicts.length > 0 && (
              // One Stack child for all state-field rows, so the rows keep
              // conflictRowSx's 8px rhythm instead of the Stack's wider gap.
              <Box>
                {conflicts.stateFieldConflicts.map((conflict, conflictIndex) => {
                  const conflictKey = stateFieldConflictKey(conflict.jurisdiction, conflict.key);
                  const groupLabel = `${conflict.label} (${conflict.jurisdiction})`;
                  const rowSx = conflictIndex === 0 ? { ...conflictRowSx, mt: 0 } : conflictRowSx;
                  return (
                    <Box key={conflictKey} sx={rowSx}>
                      <Typography variant="body2">{groupLabel}</Typography>
                      <Box role="radiogroup" aria-label={groupLabel} sx={{ display: "contents" }}>
                        {conflict.options.map((option) => (
                          <SelectableOptionCell
                            key={option.chemicalId}
                            label={`${option.display} (${option.chemicalName})`}
                            display={option.display}
                            caption={option.chemicalName}
                            selected={resolution.fieldChoices[conflictKey] === option.chemicalId}
                            onSelect={() => onFieldChoice(conflictKey, option.chemicalId)}
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Stack>
        )}
    </Paper>
  );
}

export function ResolveInventoryOverlapsStep(props: ResolveInventoryOverlapsStepProps) {
  const allResolved = props.inventoryOverlaps.every((inventoryOverlap) =>
    isInventoryOverlapResolved(
      inventoryOverlap,
      props.resolutions[inventoryOverlapKey(inventoryOverlap)],
      props.mergedHasDensity,
      props.mergedProfile
    )
  );

  // A single density applies to the whole merged chemical, so this is one
  // block for the step, not one per overlap. Keyed off the Step 1 input, not
  // mergedHasDensity, so an inline entry doesn't hide its own field.
  const needsInlineDensity =
    !props.mergedInputHasDensity &&
    props.inventoryOverlaps.some((inventoryOverlap) => {
      const resolution = props.resolutions[inventoryOverlapKey(inventoryOverlap)];
      return (
        resolution?.strategy === InventoryOverlapResolutionStrategy.Combine &&
        combineRequiresDensity(inventoryOverlap, props.mergedProfile)
      );
    });

  const facilityGroups = Object.values(groupBy(props.inventoryOverlaps, "facilityId"));

  const openOverlapStrategies = new Set(
    props.inventoryOverlaps
      .filter((inventoryOverlap) => !inventoryOverlap.isFiled)
      .map(
        (inventoryOverlap) =>
          props.resolutions[inventoryOverlapKey(inventoryOverlap)]?.strategy ?? null
      )
  );
  const batchStrategyValue =
    openOverlapStrategies.size === 1 ? (openOverlapStrategies.values().next().value ?? null) : null;

  return (
    <>
      {/* Match MergePickerContent's height so the dialog doesn't resize between steps. */}
      <DialogContent sx={{ height: "80vh", overflowY: "auto" }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {needsInlineDensity && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="flex-start"
              data-testid="combine-density-block"
            >
              <TextField
                label="Density"
                type="number"
                value={props.combineDensity.density ?? ""}
                onChange={(event) =>
                  props.onCombineDensityChange({
                    ...props.combineDensity,
                    density: safeParseFloat(event.target.value),
                  })
                }
                helperText="These entries use different units — enter a density for the merged chemical to combine them."
                sx={{ minWidth: 200 }}
                data-testid="combine-density-value"
              />
              <TextField
                select
                label="Density Units"
                value={props.combineDensity.densityUnits ?? ""}
                onChange={(event) => {
                  const value = event.target.value;
                  props.onCombineDensityChange({
                    ...props.combineDensity,
                    densityUnits: isDensityUnitsValue(value) ? value : null,
                  });
                }}
                sx={{ minWidth: 200 }}
                data-testid="combine-density-units"
              >
                {Object.values(DensityUnits).map((densityUnit) => (
                  <MenuItem key={densityUnit} value={densityUnit}>
                    {prettyPrintEnumValue(densityUnit)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
          {props.inventoryOverlaps.length > 1 &&
            props.inventoryOverlaps.some((inventoryOverlap) => !inventoryOverlap.isFiled) && (
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <Typography variant="caption" color="text.secondary">
                  Set all not-yet-filed years to:
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={batchStrategyValue}
                  onChange={(_event, strategy) => {
                    if (strategy) props.onBatchStrategyChange(strategy);
                  }}
                  data-testid="inventory-overlap-batch-strategy"
                >
                  <ToggleButton value={InventoryOverlapResolutionStrategy.Keep}>
                    Keep one
                  </ToggleButton>
                  <ToggleButton value={InventoryOverlapResolutionStrategy.Combine}>
                    Combine
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            )}
          {facilityGroups.map((groupOverlaps) => (
            <Stack key={groupOverlaps[0].facilityId} spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FactoryIcon fontSize="small" color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                  {groupOverlaps[0].facilityName}
                </Typography>
              </Stack>
              <Stack spacing={2}>
                {groupOverlaps.map((inventoryOverlap) => {
                  const key = inventoryOverlapKey(inventoryOverlap);
                  const resolution = props.resolutions[key] ?? {
                    strategy: InventoryOverlapResolutionStrategy.Keep,
                    fieldChoices: {},
                  };
                  return (
                    <InventoryOverlapCard
                      key={key}
                      inventoryOverlap={inventoryOverlap}
                      resolution={resolution}
                      onStrategyChange={(strategy) => props.onStrategyChange(key, strategy)}
                      onKeepSelect={(keepChemicalId) => props.onKeepSelect(key, keepChemicalId)}
                      onFieldChoice={(conflictKey, choice) =>
                        props.onFieldChoice(key, conflictKey, choice)
                      }
                    />
                  );
                })}
              </Stack>
            </Stack>
          ))}
          <DialogContentText component="div">
            <Typography component="p">
              You are about to create a new merged chemical for <i>{props.mergedChemicalName}</i>.
              This action will merge the selected chemicals into a single merged chemical in your
              chemical catalog and update all references to those chemicals in your facility
              inventory.{" "}
              <b>
                {props.affectedFacilityCount} {pluralize("facility", props.affectedFacilityCount)}{" "}
                will be affected.
              </b>
            </Typography>
            <Typography component="p" sx={{ mt: 1 }}>
              This action will <b>not</b> affect previously submitted reports. Dropped entries
              remain available in the merge audit record.
            </Typography>
          </DialogContentText>
        </Stack>
      </DialogContent>
      <MergeStepFooter
        onCancel={props.onCancel}
        onBack={props.onBack}
        step={props.footerStep}
        primary={{
          content: "CONTINUE",
          onClick: props.onMerge,
          disabled: !allResolved || props.isMerging,
          loading: props.isMerging,
          testId: "resolve-inventory-overlaps-merge",
        }}
      />
    </>
  );
}
